import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ThemeProvider, Typography } from "@material-ui/core";
import { Delete, Edit } from "@material-ui/icons";
import { useSnackbar } from "notistack";
import React from 'react';
import { useHistory } from "react-router-dom";
import Page from "../../containers/Page/Page";
import { useLoading } from "../../hooks/loading";
import { api } from '../../services/api';
import { ContextualTheme } from "../../themes";
import { Helpers } from '../../utils/Helpers';

interface ICliente {
    id: string;
    nome: string;
}

function Clientes() {

    //Hooks
    const history = useHistory();

    //Loading
    const { startLoading, stopLoading, isLoading } = useLoading();

    //Alert
    const { enqueueSnackbar: alert } = useSnackbar();

    //States
    const [error, setError] = React.useState<string>('');
    const [clientes, setClientes] = React.useState<ICliente[]>([]);
    const [cliente, setCliente] = React.useState<ICliente>({} as ICliente);
    const [createDialog, setCreateDialog] = React.useState<boolean>(false);

    /**
     * Ao carregar a página.
     */
    React.useEffect(() => {
        handleFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Carregar clientes.
     */
    async function handleFetch() {
        startLoading('main');
        try {
            const response = await api.get('clientes');
            setClientes(response.data.object);
            setError('');
        } catch (error: any) {
            setError(Helpers.errorFormat(error));
        } finally {
            stopLoading('main');
        }
    }

    /**
     * Salvar cliente.
     */
    async function handleSave() {
        startLoading('save');
        try {
            const response = await api.request({
                url: cliente.id ? `clientes/${cliente.id}` : 'clientes',
                method: cliente.id ? 'PUT' : 'POST',
                data: { nome: cliente.nome },
            });
            await handleFetch();
            alert(response.data.message, { variant: 'success' });
        } catch (error: any) {
            Helpers.error(error, alert);
        } finally {
            setCreateDialog(false);
            stopLoading('save');
        }
    }

    /**
     * Excluir Cliente.
     * @param cliente 
     * @return void
     */
    async function handleDelete(cliente: ICliente) {
        if (!isLoading(`delete-${cliente.id}`)) {
            startLoading(`delete-${cliente.id}`);
            try {
                const response = await api.delete(`clientes/${cliente.id}`);
                let newData = clientes.filter(i => i.id !== cliente.id);
                setClientes(newData);
                alert(response.data.message, { variant: 'success' });
            } catch (error) {
                Helpers.error(error, alert);
            } finally {
                stopLoading(`delete-${cliente.id}`);
            }

        }
    }

    /**
     * Criar cliente.
     */
    function handleCreate() {
        setCliente({ id: '', nome: '' });
        setCreateDialog(true);
    }

    /**
     * Editar cliente.
     * @param cliente 
     */
    function handleEdit(cliente: ICliente) {
        setCliente(cliente);
        setCreateDialog(true);
    }

    return (
        <Page error={error} loading={isLoading('main')}>
            <Page.Title>
                <Typography variant="h6" component="div">
                    Clientes
                </Typography>

                <Button
                    color="primary"
                    variant="contained"
                    onClick={() => handleCreate()}>
                    Novo Cliente
                </Button>
                <Dialog fullWidth={true} maxWidth="xs" open={createDialog} onClose={() => setCreateDialog(false)}>
                    <DialogTitle>{cliente.id ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
                    <DialogContent>
                        <TextField
                            margin="dense"
                            label="Nome do Cliente"
                            autoFocus={true}
                            fullWidth={true}
                            value={cliente.nome}
                            onChange={(event) => setCliente({ ...cliente, nome: event.target.value })}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color="primary"
                            disabled={isLoading('save')}
                            onClick={() => setCreateDialog(false)}>
                            Cancelar
                        </Button>
                        <Button
                            color="primary"
                            startIcon={isLoading('save') ? <CircularProgress color="inherit" size="15px" /> : undefined}
                            disabled={isLoading('save')}
                            onClick={() => handleSave()}>
                            {cliente.id ? 'Atualizar' : 'Adicionar'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Page.Title>
            {clientes &&
                <Page.Section>
                    <Page.Content>
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Cliente</TableCell>
                                        <TableCell align="center">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {clientes.map((cliente) => (
                                        <TableRow
                                            key={cliente.id}
                                            hover={true}
                                        >
                                            <TableCell onClick={() => history.push(`/descontos/cliente/${cliente.id}`)}>{cliente.nome}</TableCell>
                                            <TableCell align="center" width={100}>
                                                <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                                                    <Box mr={2}>
                                                        <IconButton size="small" color="primary" onClick={() => handleEdit(cliente)}>
                                                            <Edit />
                                                        </IconButton>
                                                    </Box>
                                                    <ThemeProvider theme={ContextualTheme}>
                                                        {isLoading(`delete-${cliente.id}`) ?
                                                            <CircularProgress color="secondary" size={16} />
                                                            :
                                                            <IconButton size="small" color="secondary" onClick={() => handleDelete(cliente)}>
                                                                <Delete />
                                                            </IconButton>
                                                        }
                                                    </ThemeProvider>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Page.Content>
                </Page.Section>
            }
        </Page >
    );
}

export default Clientes;